import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { ERAS, STATS } from "../data/resume";

const NODE_POSITIONS = [
  { x: 320, y: 700 },
  { x: 780, y: 480 },
  { x: 1220, y: 640 },
  { x: 1640, y: 400 },
];

// Scene 1 — Map (5–12%): the journey path with 4 glowing era nodes and
// stat counters; ends with a camera dive into the first node.
const Scene1Map: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const pathDraw = interpolate(frame, [4, 34], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Camera dive into the first node at the end of the scene.
  const dive = interpolate(frame, [50, 65], [1, 3.4], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#181818",
        opacity: interpolate(frame, [0, 6, 58, 65], [0, 1, 1, 0], {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
        }),
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          scale: String(dive),
          transformOrigin: `${NODE_POSITIONS[0].x}px ${NODE_POSITIONS[0].y}px`,
        }}
      >
        <svg
          width={1920}
          height={1080}
          viewBox="0 0 1920 1080"
          style={{ position: "absolute", inset: 0 }}
        >
          <path
            d={`M ${NODE_POSITIONS[0].x} ${NODE_POSITIONS[0].y}
                C 480 560, 620 460, ${NODE_POSITIONS[1].x} ${NODE_POSITIONS[1].y}
                S 1080 700, ${NODE_POSITIONS[2].x} ${NODE_POSITIONS[2].y}
                S 1500 380, ${NODE_POSITIONS[3].x} ${NODE_POSITIONS[3].y}`}
            fill="none"
            stroke="#fcaeea"
            strokeWidth={5}
            strokeDasharray={1}
            pathLength={1}
            strokeDashoffset={pathDraw}
            strokeLinecap="round"
            opacity={0.7}
          />
        </svg>
        {ERAS.map((era, i) => {
          const pop = spring({
            frame: frame - (8 + i * 8),
            fps,
            config: { damping: 12, stiffness: 170 },
          });
          const pos = NODE_POSITIONS[i];
          return (
            <div
              key={era.id}
              style={{
                position: "absolute",
                left: pos.x,
                top: pos.y,
                translate: "-50% -50%",
                scale: String(pop),
                textAlign: "center",
              }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 22,
                  backgroundColor: era.color,
                  margin: "0 auto",
                  boxShadow: `0 0 ${
                    30 + Math.sin(frame / 5 + i) * 12
                  }px ${era.color}`,
                }}
              />
              <div
                style={{
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 600,
                  fontSize: 30,
                  color: "white",
                  marginTop: 16,
                  whiteSpace: "nowrap",
                }}
              >
                {era.label}
              </div>
              <div
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 22,
                  color: era.color,
                  marginTop: 4,
                }}
              >
                {era.years}
              </div>
            </div>
          );
        })}
      </div>
      <div
        style={{
          position: "absolute",
          top: 120,
          left: 0,
          right: 0,
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          gap: 110,
          opacity: interpolate(frame, [10, 20, 48, 56], [0, 1, 1, 0], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          }),
        }}
      >
        {STATS.map((stat, i) => (
          <div key={stat.label} style={{ textAlign: "center" }}>
            <div
              style={{
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 600,
                fontSize: 92,
                color: "white",
              }}
            >
              {Math.round(
                interpolate(frame, [12 + i * 4, 34 + i * 4], [0, stat.value], {
                  extrapolateLeft: "clamp",
                  extrapolateRight: "clamp",
                })
              )}
              {stat.suffix}
            </div>
            <div
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 26,
                color: "#b3b3b3",
              }}
            >
              {stat.label}
            </div>
          </div>
        ))}
      </div>
    </AbsoluteFill>
  );
};

export default Scene1Map;
